import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, Download, Share2, Search, Filter, ChevronDown, XCircle, CheckCircle, Clock as ClockIcon, Info, Ticket } from 'lucide-react';
import { format, isPast, isFuture } from 'date-fns';

import { registrationService } from '../../services/registrationService';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import RefundProgressTracker from './components/RefundProgressTracker';

const statusColors = {
  approved: 'bg-green-100 text-green-800 border-green-300',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
};

const statusLabels = {
  approved: 'Onaylandı',
  pending: 'Beklemede',
  rejected: 'Reddedildi',
  cancelled: 'İptal Edildi'
};

const cancellationStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300'
};

const cancellationStatusLabels = {
  pending: 'İptal Talebi Beklemede',
  approved: 'İptal Onaylandı',
  rejected: 'İptal Reddedildi'
};

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [cancellingTicket, setCancellingTicket] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [ticketToCancel, setTicketToCancel] = useState(null);
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await registrationService?.getMyRegistrations();
      setTickets(data);
    } catch (err) {
      setError(err?.message || 'Biletler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const canCancelRegistration = (ticket) => {
    if (ticket?.status !== 'approved') return false;
    
    const eventDateTime = new Date(`${ticket?.event?.eventDate}T${ticket?.event?.eventTime}`);
    const now = new Date();
    const hoursUntilEvent = (eventDateTime - now) / (1000 * 60 * 60);
    
    return hoursUntilEvent >= 24;
  };

  const getTimeUntilEvent = (ticket) => {
    const eventDateTime = new Date(`${ticket?.event?.eventDate}T${ticket?.event?.eventTime}`);
    const now = new Date();
    const hoursUntilEvent = Math.floor((eventDateTime - now) / (1000 * 60 * 60));
    
    if (hoursUntilEvent < 0) return 'Geçmiş';
    if (hoursUntilEvent < 24) return `${hoursUntilEvent} saat kaldı`;
    
    const daysUntilEvent = Math.floor(hoursUntilEvent / 24);
    return `${daysUntilEvent} gün kaldı`;
  };

  const handleCancelClick = (ticket) => {
    setTicketToCancel(ticket);
    setCancellationReason('');
    setShowCancelRequestModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!ticketToCancel) return;
    if (!cancellationReason?.trim()) {
      setError('Lütfen iptal sebebini belirtin');
      return;
    }

    try {
      setCancellingTicket(ticketToCancel?.id);
      await registrationService?.requestCancellation(ticketToCancel?.id, cancellationReason);
      
      // Reload tickets to get updated status
      await loadTickets();
      
      setShowCancelRequestModal(false);
      setTicketToCancel(null);
      setCancellationReason('');
    } catch (err) {
      setError(err?.message || 'İptal talebi oluşturulurken bir hata oluştu');
    } finally {
      setCancellingTicket(null);
    }
  };

  const getCancellationStatus = (ticket) => {
    if (!ticket?.cancellationRequestedAt) return null;
    
    if (ticket?.cancellationProcessedAt) {
      // Check if cancellation was approved (status changed to cancelled) or rejected
      return ticket?.status === 'cancelled' ? 'approved' : 'rejected';
    }
    
    return 'pending';
  };

  const getRefundTimelineMessage = (ticket) => {
    const cancellationStatus = getCancellationStatus(ticket);
    
    if (cancellationStatus === 'approved') {
      return 'İade işlemi 5-7 iş günü içinde tamamlanacaktır.';
    }
    
    if (cancellationStatus === 'pending') {
      return 'İptal talebiniz onaylandığında iade süreci başlatılacaktır.';
    }
    
    return null;
  };

  // Calculate refund amount based on event timing and pricing
  const calculateRefundAmount = (ticket) => {
    if (!ticket?.event) return 0;
    const hoursUntilEvent = (new Date(ticket?.event?.eventDate)?.getTime() - new Date()?.getTime()) / (1000 * 60 * 60);
    const basePrice = ticket?.equipmentRental ? 
      (ticket?.event?.priceWithRental || 2000) : 
      (ticket?.event?.priceOwnBoard || 500);
    
    if (hoursUntilEvent >= 72) return basePrice;
    if (hoursUntilEvent >= 48) return Math.round(basePrice * 0.75);
    if (hoursUntilEvent >= 24) return Math.round(basePrice * 0.5);
    return 0;
  };

  const getTicketStatus = (ticket) => {
    if (!ticket?.event?.eventDate) return 'upcoming';
    
    const eventDate = new Date(ticket?.event?.eventDate);
    const now = new Date();
    
    if (isPast(eventDate)) return 'completed';
    if (ticket?.status === 'approved') return 'upcoming';
    return ticket?.status;
  };

  const filteredTickets = tickets?.filter(ticket => {
    // Search filter
    const matchesSearch = ticket?.event?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         ticket?.event?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || ticket?.status === statusFilter;
    
    // Time filter
    let matchesTime = true;
    if (timeFilter === 'upcoming') {
      matchesTime = ticket?.event?.eventDate && isFuture(new Date(ticket?.event?.eventDate));
    } else if (timeFilter === 'past') {
      matchesTime = ticket?.event?.eventDate && isPast(new Date(ticket?.event?.eventDate));
    }
    
    // Archive filter
    const isArchived = getTicketStatus(ticket) === 'completed';
    const matchesArchive = showArchived ? isArchived : !isArchived;
    
    return matchesSearch && matchesStatus && matchesTime && matchesArchive;
  });

  const downloadTicketPDF = (ticket) => {
    // Create a printable ticket
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(`
      <html>
        <head>
          <title>Bilet - ${ticket?.event?.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .ticket { border: 2px solid #000; padding: 20px; max-width: 600px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
            .qr-code { text-align: center; margin: 20px 0; }
            .details { margin: 20px 0; }
            .detail-row { margin: 10px 0; }
            .label { font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="header">
              <h1>${ticket?.event?.title}</h1>
              <p>Etkinlik Bileti</p>
            </div>
            <div class="details">
              <div class="detail-row">
                <span class="label">Katılımcı:</span> ${ticket?.participantName}
              </div>
              <div class="detail-row">
                <span class="label">E-posta:</span> ${ticket?.participantEmail}
              </div>
              <div class="detail-row">
                <span class="label">Tarih:</span> ${format(new Date(ticket?.event?.eventDate), 'dd MMMM yyyy')}
              </div>
              <div class="detail-row">
                <span class="label">Saat:</span> ${ticket?.event?.eventTime}
              </div>
              <div class="detail-row">
                <span class="label">Konum:</span> ${ticket?.event?.location}
              </div>
              <div class="detail-row">
                <span class="label">Rezervasyon No:</span> ${ticket?.id}
              </div>
            </div>
            <div class="qr-code">
              <img src="${document.getElementById(`qr-${ticket?.id}`)?.querySelector('svg')?.outerHTML}" />
              <p>QR Kod ile Giriş</p>
            </div>
          </div>
          <script>window.print(); window.close();</script>
        </body>
      </html>
    `);
  };

  const shareTicket = async (ticket) => {
    const shareData = {
      title: `${ticket?.event?.title} - Etkinlik Bileti`,
      text: `${ticket?.event?.title} etkinliğine katılacağım! ${format(new Date(ticket?.event?.eventDate), 'dd MMMM yyyy')} tarihinde görüşmek üzere.`,
      url: window.location?.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Paylaşım iptal edildi');
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard?.writeText(`${shareData?.text}\n${shareData?.url}`);
      alert('Bilet bilgisi kopyalandı!');
    }
  };

  const addToCalendar = (ticket) => {
    const event = ticket?.event;
    const startDate = new Date(`${event?.eventDate}T${event?.eventTime}`);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hours later

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event?.title)}&dates=${startDate?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0]}Z/${endDate?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0]}Z&details=${encodeURIComponent(event?.title)}&location=${encodeURIComponent(event?.location)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pt-[60px]">
        <Header />
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pt-[60px]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Biletlerim</h1>
          <p className="text-gray-600">Etkinlik rezervasyonlarınız ve QR kod biletleriniz</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Etkinlik veya konum ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="approved">Onaylandı</option>
              <option value="pending">Beklemede</option>
              <option value="rejected">Reddedildi</option>
              <option value="cancelled">İptal Edildi</option>
            </select>

            {/* Time Filter */}
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tüm Zamanlar</option>
              <option value="upcoming">Yaklaşan</option>
              <option value="past">Geçmiş</option>
            </select>

            {/* Archive Toggle */}
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                showArchived
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {showArchived ? 'Arşiv Gizle' : 'Arşivi Göster'}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Tickets List */}
        {filteredTickets?.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Bilet bulunamadı</h3>
            <p className="text-gray-600">
              Aradığınız kriterlere uygun bilet bulunmamaktadır.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredTickets?.map((ticket) => {
              const cancellationStatus = getCancellationStatus(ticket);
              const refundMessage = getRefundTimelineMessage(ticket);
              
              return (
              <div
                key={ticket?.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                {/* Ticket Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1 pr-2">
                      {ticket?.event?.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors?.[ticket?.status]}`}>
                      {statusLabels?.[ticket?.status]}
                    </span>
                  </div>

                  {/* Cancellation Status Badge - NEW */}
                  {cancellationStatus && (
                    <div className="mb-4">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cancellationStatusColors?.[cancellationStatus]}`}>
                        {cancellationStatus === 'pending' && <ClockIcon className="w-4 h-4" />}
                        {cancellationStatus === 'approved' && <CheckCircle className="w-4 h-4" />}
                        {cancellationStatus === 'rejected' && <XCircle className="w-4 h-4" />}
                        <span className="text-xs font-medium">
                          {cancellationStatusLabels?.[cancellationStatus]}
                        </span>
                      </div>
                      
                      {/* Cancellation Details - NEW */}
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <p>
                          <span className="font-medium">Talep Tarihi:</span>{' '}
                          {ticket?.cancellationRequestedAt && 
                            format(new Date(ticket?.cancellationRequestedAt), 'dd MMMM yyyy HH:mm')}
                        </p>
                        
                        {ticket?.cancellationRequestReason && (
                          <p>
                            <span className="font-medium">Sebep:</span>{' '}
                            {ticket?.cancellationRequestReason}
                          </p>
                        )}

                        {ticket?.cancellationProcessedAt && (
                          <p>
                            <span className="font-medium">İşlem Tarihi:</span>{' '}
                            {format(new Date(ticket?.cancellationProcessedAt), 'dd MMMM yyyy HH:mm')}
                          </p>
                        )}

                        {ticket?.organizerCancellationResponse && (
                          <div className="mt-2 p-2 bg-gray-50 rounded">
                            <p className="font-medium mb-1">Organizatör Yanıtı:</p>
                            <p className="text-gray-700">{ticket?.organizerCancellationResponse}</p>
                          </div>
                        )}

                        {/* Refund Timeline - NEW */}
                        {refundMessage && (
                          <div className="mt-2 flex items-start gap-2 p-2 bg-blue-50 rounded">
                            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-blue-800">{refundMessage}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {ticket?.event?.eventDate && format(new Date(ticket?.event?.eventDate), 'dd MMMM yyyy')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{ticket?.event?.eventTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{ticket?.event?.location}</span>
                    </div>
                  </div>
                </div>

                {/* Ticket Content */}
                <div className="p-6">
                  {/* Ticket Details */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-600">Katılımcı</p>
                      <p className="font-medium text-gray-900">{ticket?.participantName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">E-posta</p>
                      <p className="font-medium text-gray-900">{ticket?.participantEmail}</p>
                    </div>
                    {ticket?.participantPhone && (
                      <div>
                        <p className="text-gray-600">Telefon</p>
                        <p className="font-medium text-gray-900">{ticket?.participantPhone}</p>
                      </div>
                    )}
                    {ticket?.equipmentRental && (
                      <div>
                        <p className="text-gray-600">Ekipman Kiralama</p>
                        <p className="font-medium text-gray-900">Evet</p>
                      </div>
                    )}
                    {ticket?.specialRequirements && (
                      <div>
                        <p className="text-gray-600">Özel İstekler</p>
                        <p className="font-medium text-gray-900">{ticket?.specialRequirements}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-600">Rezervasyon Tarihi</p>
                      <p className="font-medium text-gray-900">
                        {ticket?.createdAt && format(new Date(ticket?.createdAt), 'dd MMMM yyyy HH:mm')}
                      </p>
                    </div>
                    {ticket?.approvedAt && (
                      <div>
                        <p className="text-gray-600">Onay Tarihi</p>
                        <p className="font-medium text-gray-900">
                          {format(new Date(ticket?.approvedAt), 'dd MMMM yyyy HH:mm')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Refund Progress Tracker - Show for cancelled tickets */}
                  {ticket?.status === 'cancelled' && ticket?.refundStatus && (
                    <div className="mt-6">
                      <RefundProgressTracker
                        refundStatus={ticket?.refundStatus}
                        paymentMethod={ticket?.paymentMethod}
                        expectedArrivalDate={ticket?.expectedArrivalDate}
                        refundAmount={calculateRefundAmount(ticket)}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 mt-6">
                    <button
                      onClick={() => downloadTicketPDF(ticket)}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Download className="w-4 h-4" />
                      İndir
                    </button>
                    <button
                      onClick={() => shareTicket(ticket)}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    >
                      <Share2 className="w-4 h-4" />
                      Paylaş
                    </button>
                  </div>

                  <button
                    onClick={() => addToCalendar(ticket)}
                    className="w-full mt-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Takvime Ekle
                  </button>

                  {/* Cancellation Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {canCancelRegistration(ticket) ? (
                      <>
                        <p className="text-xs text-gray-600 mb-2 text-center">
                          {getTimeUntilEvent(ticket)} - İptal talebi oluşturulabilir
                        </p>
                        <button
                          onClick={() => handleCancelClick(ticket)}
                          disabled={cancellingTicket === ticket?.id}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XCircle className="w-4 h-4" />
                          {cancellingTicket === ticket?.id ? 'Talep Oluşturuluyor...' : 'İptal Talebi Oluştur'}
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 text-center">
                        {getTimeUntilEvent(ticket)} - İptal talebi oluşturulamaz
                      </p>
                    )}
                  </div>
                </div>

                {/* Ticket Details Toggle */}
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => setExpandedTicket(expandedTicket === ticket?.id ? null : ticket?.id)}
                    className="w-full px-6 py-3 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <span>Detayları Göster</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        expandedTicket === ticket?.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {expandedTicket === ticket?.id && (
                    <div className="px-6 pb-6 pt-2 space-y-3 text-sm">
                      <div>
                        <p className="text-gray-600">Katılımcı</p>
                        <p className="font-medium text-gray-900">{ticket?.participantName}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">E-posta</p>
                        <p className="font-medium text-gray-900">{ticket?.participantEmail}</p>
                      </div>
                      {ticket?.participantPhone && (
                        <div>
                          <p className="text-gray-600">Telefon</p>
                          <p className="font-medium text-gray-900">{ticket?.participantPhone}</p>
                        </div>
                      )}
                      {ticket?.equipmentRental && (
                        <div>
                          <p className="text-gray-600">Ekipman Kiralama</p>
                          <p className="font-medium text-gray-900">Evet</p>
                        </div>
                      )}
                      {ticket?.specialRequirements && (
                        <div>
                          <p className="text-gray-600">Özel İstekler</p>
                          <p className="font-medium text-gray-900">{ticket?.specialRequirements}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-600">Rezervasyon Tarihi</p>
                        <p className="font-medium text-gray-900">
                          {ticket?.createdAt && format(new Date(ticket?.createdAt), 'dd MMMM yyyy HH:mm')}
                        </p>
                      </div>
                      {ticket?.approvedAt && (
                        <div>
                          <p className="text-gray-600">Onay Tarihi</p>
                          <p className="font-medium text-gray-900">
                            {format(new Date(ticket?.approvedAt), 'dd MMMM yyyy HH:mm')}
                          </p>
                        </div>
                      )}
                      <button
                        onClick={() => navigate(`/event-details/${ticket?.eventId}`)}
                        className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Etkinlik Detaylarını Gör
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </main>
      {/* Cancellation Request Modal - UPDATED */}
      {showCancelRequestModal && ticketToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              İptal Talebi Oluştur
            </h3>
            <p className="text-gray-600 mb-4">
              <strong>{ticketToCancel?.event?.title}</strong> etkinliği için neden iptal etmek istiyorsunuz?
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                İptal Sebebi <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e?.target?.value)}
                placeholder="İptal sebebinizi açıklayın..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <p className="font-medium mb-1">İptal Talebi Süreci:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Talebiniz organizatör tarafından incelenecektir</li>
                    <li>Onay durumu hakkında bilgilendirileceksiniz</li>
                    <li>Onaylanan talepler için iade 5-7 iş günü içinde yapılacaktır</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                Bu işlem geri alınamaz. İptal talebi oluşturulduktan sonra organizatör onayı bekleyecektir.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelRequestModal(false);
                  setTicketToCancel(null);
                  setCancellationReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Vazgeç
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={cancellingTicket === ticketToCancel?.id || !cancellationReason?.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancellingTicket === ticketToCancel?.id ? 'Talep Oluşturuluyor...' : 'Talep Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}